import * as AlertBarState from '../../components/alert-bar/AlertBar.state';
import * as Nested from '../../utils/Nested';
import * as ModalState from '../../components/modal/Modal.state';
import * as R from 'ramda';
import { replaceAlerts } from '../../components/alert-bar/AlertBar.state';
import { DEFAULT_PAYMENT_VALUE } from './CheckoutForm.state';

import {
  CardCheckoutForm,
  CashCheckoutForm,
  CheckCheckoutForm,
  WirePaymentCheckoutForm,
  PayLinkCheckoutForm,
} from './components';

export const checkScanMaxAttempts = 30;
export const maxSecondsToInsertCard = 45;
export const maxSecondsToInsertCardDps = 240;

/**
 * @typedef {Object} InitialState
 *
 * Check Payment State
 *
 * @property timeRemainingToInsertCheck: Controls the countdown modal timer state and is intended to mirror the check scanner hardware timeout.
 *
 * @property isPollingForCheckResponse: Awaiting check from user to be inserted.
 *
 * @property isCheckProcessing: Check has been accepted. Awaiting async backend and hardware operations.
 *
 * @property isPollingTimeExtended:
 * In the case where the user inserts a check between 0-10 seconds, the polled payment status may not update before the countdown times out.
 * Add an additional N seconds for processing and hardware delays.
 *
 * @property visitId:
 * Provided by Ghenghis. For Datacap (TranCloud) payments, this is passed as a parameter to CB_POS.
 * It is then used by Device Payment Service (DPS) to generate a payment_attempt_unique_identifier.
 * pos-frontend then uses this unique identifier when polling for Datacap (TranCloud) payment status updates.
 *
 * @todo Document remaining properties.
 */

const initialState = {
  error: false,
  isLoading: false,
  isPaymentError: false,
  isPaymentSuccess: false,
  isPayLinkError: false,
  isAwaitingCreditCard: false,
  cardPaymentType: null, // will become 'automatic' or 'manual'
  timeRemainingToUseCard: maxSecondsToInsertCard,
  order: {},
  serviceFeeForCurrentAmount: null,
  convenienceFee: null,
  maxCardAmountAllowed: null,
  timeRemainingToInsertCheck: checkScanMaxAttempts,
  isPollingForCheckResponse: false,
  isCheckProcessing: false,
  isPollingTimeExtended: false,
  checkScanFeedback: null,
  visitId: null,
  isPollingForCCResponse: false,
  paymentAttemptUniqueId: null,
  isCancelingTransaction: false,
};

// Payment Actions
export const INITIATE_PAYMENT = 'INITIATE_PAYMENT';
export const initiatePayment = (data) => {
  return {
    type: INITIATE_PAYMENT,
    payload: data,
  };
};

export const PAYMENT_SUCCESS = 'PAYMENT_SUCCESS';
export const paymentSuccess = (data) => ({
  type: PAYMENT_SUCCESS,
  payload: data,
});

export const PAYMENT_ERROR = 'PAYMENT_ERROR';
export const paymentError = (data) => ({
  type: PAYMENT_ERROR,
  payload: data,
});

export const INITIATE_PAYLINK = 'INITIATE_PAYLINK';
export const initiatePayLink = (data) => {
  return {
    type: INITIATE_PAYLINK,
    payload: data,
  };
};

export const PAYLINK_SUCCESS = 'PAYLINK_SUCCESS';
export const paylinkSuccess = (data) => ({
  type: PAYLINK_SUCCESS,
  payload: data,
});

export const PAYLINK_ERROR = 'PAYLINK_ERROR';
export const paylinkError = (data) => ({
  type: PAYMENT_ERROR,
  payload: data,
});

export const SET_INSERT_CHECK_TIME_REMAINING =
  'SET_INSERT_CHECK_TIME_REMAINING';
export const setInsertCheckTimeRemaining = (timeRemaining) => {
  return {
    type: SET_INSERT_CHECK_TIME_REMAINING,
    payload: timeRemaining,
  };
};
export const CANCEL_TRANSACTION_REQUEST = 'CANCEL_TRANSACTION_REQUEST';
export const cancelTransactionRequest = (data) => {
  return {
    type: CANCEL_TRANSACTION_REQUEST,
    payload: data,
  };
};

export const CANCEL_TRANSACTION_SUCCESS = 'CANCEL_TRANSACTION_SUCCESS';
export const cancelTransactionSuccess = () => {
  return {
    type: CANCEL_TRANSACTION_SUCCESS,
  };
};

export const CANCEL_TRANSACTION_FAILURE = 'CANCEL_TRANSACTION_FAILURE';
export const cancelTransactionFailure = () => {
  return {
    type: CANCEL_TRANSACTION_FAILURE,
  };
};

export const INITIATE_CHECK_SCAN = 'INITIATE_CHECK_SCAN';
export const initiateCheckScan = (data) => {
  return {
    type: INITIATE_CHECK_SCAN,
    payload: data,
  };
};

export const IS_POLLING_FOR_CHECK_RESPONSE = 'IS_POLLING_FOR_CHECK_RESPONSE';
export const isPollingForCheckResponse = (data) => {
  return {
    type: IS_POLLING_FOR_CHECK_RESPONSE,
    payload: data,
  };
};

export const IS_CHECK_PROCESSING = 'IS_CHECK_PROCESSING';
export const isCheckProcessing = (bool) => {
  return {
    type: IS_CHECK_PROCESSING,
    payload: bool,
  };
};

export const IS_POLLING_TIME_EXTENDED = 'IS_POLLING_TIME_EXTENDED';
export const isPollingTimeExtended = (bool) => {
  return {
    type: IS_POLLING_TIME_EXTENDED,
    payload: bool,
  };
};

export const MAKE_CHECK_PAYMENT_ERROR = 'MAKE_CHECK_PAYMENT_ERROR';
export const makeCheckPaymentError = (data) => {
  return {
    type: MAKE_CHECK_PAYMENT_ERROR,
    payload: data,
  };
};

export const SET_CHECK_SCAN_FEEDBACK = 'SET_CHECK_SCAN_FEEDBACK';
export const setCheckScanFeedback = (message) => {
  return {
    type: SET_CHECK_SCAN_FEEDBACK,
    payload: message,
  };
};

export const STOP_CHECK_SCAN_POLL = 'STOP_CHECK_SCAN_POLL';
export const stopCheckScanPoll = () => {
  return {
    type: STOP_CHECK_SCAN_POLL,
  };
};

export const IS_AWAITING_CREDIT_CARD = 'IS_AWAITING_CREDIT_CARD';
export const isAwaitingCreditCard = (cardPaymentType) => {
  return {
    type: IS_AWAITING_CREDIT_CARD,
    payload: cardPaymentType,
  };
};

export const STOP_AWAITING_CREDIT_CARD = 'STOP_AWAITING_CREDIT_CARD';
export const stopAwaitingCreditCard = () => {
  return {
    type: STOP_AWAITING_CREDIT_CARD,
  };
};

export const SET_CARD_PAYMENT_TYPE = 'SET_CARD_PAYMENT_TYPE';
export const setCardPaymentType = (cardPaymentType) => {
  return {
    type: SET_CARD_PAYMENT_TYPE,
    payload: cardPaymentType,
  };
};

export const SET_AWAIT_CREDIT_CARD_TIME_REMAINING =
  'SET_AWAIT_CREDIT_CARD_TIME_REMAINING';
export const setAwaitCreditCardTimeRemaining = (timeRemaining) => {
  return {
    type: SET_AWAIT_CREDIT_CARD_TIME_REMAINING,
    payload: timeRemaining,
  };
};

export const GET_SERVICE_FEE = 'GET_SERVICE_FEE';
export const getServiceFee = (amount) => {
  return {
    type: GET_SERVICE_FEE,
    payload: amount,
  };
};
export const GET_SERVICE_FEE_SUCCESS = 'GET_SERVICE_FEE_SUCCESS';
export const getServiceFeeSuccess = (data) => ({
  type: GET_SERVICE_FEE_SUCCESS,
  payload: data,
});

export const GET_SERVICE_FEE_FAILURE = 'GET_SERVICE_FEE_FAILURE';
export const getServiceFeeError = (data) => ({
  type: GET_SERVICE_FEE_FAILURE,
  payload: data,
});

export const GET_CONVENIENCE_FEE = 'GET_CONVENIENCE_FEE';
export const getConvenienceFee = (data) => {
  return {
    type: GET_CONVENIENCE_FEE,
    payload: data,
  };
};
export const GET_CONVENIENCE_FEE_SUCCESS = 'GET_CONVENIENCE_FEE_SUCCESS';
export const getConvenienceFeeSuccess = (data) => ({
  type: GET_CONVENIENCE_FEE_SUCCESS,
  payload: data,
});

export const GET_CONVENIENCE_FEE_FAILURE = 'GET_CONVENIENCE_FEE_FAILURE';
export const getConvenienceFeeError = (data) => ({
  type: GET_CONVENIENCE_FEE_FAILURE,
  payload: data,
});

// GET_PAYMENT_BY_ID actions used for polling the payment status when scanning a check

export const GET_PAYMENT_BY_ID = 'GET_PAYMENT_BY_ID';
export const initiateGetPaymentById = () => {
  return {
    type: GET_PAYMENT_BY_ID,
  };
};

export const GET_PAYMENT_BY_ID_SUCCESS = 'GET_PAYMENT_BY_ID_SUCCESS';
export const getPaymentByIdSuccess = (data) => {
  return {
    type: GET_PAYMENT_BY_ID_SUCCESS,
    payload: data,
  };
};

export const GET_PAYMENT_BY_ID_ERROR = 'GET_PAYMENT_BY_ID_ERROR';
export const getPaymentByIdError = (data) => {
  return {
    type: GET_PAYMENT_BY_ID_ERROR,
    payload: data,
  };
};

export const INITIATE_POST_VISIT = 'INITIATE_POST_VISIT';
export const initiatePostVisitId = ({ order_id }) => ({
  type: INITIATE_POST_VISIT,
  payload: order_id,
});
export const RETRY_INITIATE_POST_VISIT = 'RETRY_INITIATE_POST_VISIT';
export const retryInitiatePostVisitId = ({ order_id, retryNumber }) => ({
  type: RETRY_INITIATE_POST_VISIT,
  payload: { order_id, retryNumber },
});
export const POST_VISIT_SUCCESS = 'POST_VISIT_SUCCESS';
export const postVisitIdSuccess = (data) => ({
  type: POST_VISIT_SUCCESS,
  payload: data,
});
export const POST_VISIT_ERROR = 'POST_VISIT_ERROR';
export const postVisitIdError = (data) => ({
  type: POST_VISIT_ERROR,
  payload: data,
});

export const SET_PAYMENT_UNIQUE_IDENTIFIER = 'SET_PAYMENT_UNIQUE_IDENTIFIER';
export const setPaymentUniqueIdentifier = ({ paymentAttemptUniqueId }) => {
  return {
    type: SET_PAYMENT_UNIQUE_IDENTIFIER,
    payload: { paymentAttemptUniqueId },
  };
};

export const START_POLLING_FOR_CC_RESPONSE = 'START_POLLING_FOR_CC_RESPONSE';
export const startPollingForCCResponse = ({ navigate, location }) => {
  return {
    type: START_POLLING_FOR_CC_RESPONSE,
    payload: {
      navigate,
      location,
    },
  };
};

export const STOP_POLLING_FOR_CC_RESPONSE = 'STOP_POLLING_FOR_CC_RESPONSE';
export const stopPollingForCCResponse = () => {
  return {
    type: STOP_POLLING_FOR_CC_RESPONSE,
  };
};

export const POLLING_CC_SUCCESS = 'POLLING_CC_SUCCESS';
export const pollingCCSuccess = () => {
  return {
    type: POLLING_CC_SUCCESS,
  };
};

export const POLLING_CC_ERROR = 'POLLING_CC_ERROR';
export const pollingCCError = () => {
  return {
    type: POLLING_CC_ERROR,
  };
};

// Alert Bar Nested Actions
export const ALERT_BAR_ACTION = 'checkout/ALERT_BAR_ACTION';
export const checkoutAlertBarAction = Nested.nestedAction(ALERT_BAR_ACTION);

// Modal Nested Actions
export const MODAL_ACTION = 'checkout/MODAL_ACTION';
export const checkoutModalAction = Nested.nestedAction(MODAL_ACTION);

export const CARD_CHECKOUT_FORM_ACTION = 'checkout/CARD_CHECKOUT_FORM_ACTION';
export const cardCheckoutFormAction = Nested.nestedAction(
  CARD_CHECKOUT_FORM_ACTION,
);

export const CHECK_CHECKOUT_FORM_ACTION = 'checkout/CHECK_CHECKOUT_FORM_ACTION';
export const checkCheckoutFormAction = Nested.nestedAction(
  CHECK_CHECKOUT_FORM_ACTION,
);

export const CASH_CHECKOUT_FORM_ACTION = 'checkout/CASH_CHECKOUT_FORM_ACTION';
export const cashCheckoutFormAction = Nested.nestedAction(
  CASH_CHECKOUT_FORM_ACTION,
);

export const WIRE_PAYMENT_CHECKOUT_FORM_ACTION =
  'checkout/WIRE_PAYMENT_CHECKOUT_FORM_ACTION';
export const wirePaymentCheckoutFormAction = Nested.nestedAction(
  WIRE_PAYMENT_CHECKOUT_FORM_ACTION,
);

export const PAYLINK_CHECKOUT_FORM_ACTION =
  'checkout/PAYLINK_CHECKOUT_FORM_ACTION';
export const paylinkCheckoutFormAction = Nested.nestedAction(
  PAYLINK_CHECKOUT_FORM_ACTION,
);

// Raw Reducer
export const _reducer = (state = initialState, { type, payload } = action) => {
  switch (type) {
    case POST_VISIT_SUCCESS:
      return {
        ...state,
        visitId: payload.visit_id,
        isPollingForCCResponse: false,
      };
    case INITIATE_PAYMENT:
      return {
        ...state,
        isLoading: true,
      };
    case PAYMENT_SUCCESS:
      return {
        ...state,
        isPaymentSuccess: true,
        isLoading: false,
      };
    case PAYMENT_ERROR:
      return {
        ...state,
        isPaymentError: true,
        isLoading: false,
      };
    case INITIATE_PAYLINK:
      return {
        ...state,
        isLoading: true,
      };
    case PAYLINK_SUCCESS:
      return {
        ...state,
        isPayLinkError: false,
        isLoading: false,
      };
    case PAYLINK_ERROR:
      return {
        ...state,
        isPayLinkError: true,
        isLoading: false,
      };
    case CANCEL_TRANSACTION_REQUEST:
      return {
        ...state,
        isLoading: false,
        isCancelingTransaction: true,
      };
    case CANCEL_TRANSACTION_SUCCESS:
      return {
        ...state,
        isCancelingTransaction: false,
      };
    case CANCEL_TRANSACTION_FAILURE:
      return {
        ...state,
        isCancelingTransaction: false,
      };
    case INITIATE_CHECK_SCAN:
      return {
        ...state,
        isPollingForCheckResponse: true,
      };
    case IS_POLLING_FOR_CHECK_RESPONSE:
      return {
        ...state,
        isPollingForCheckResponse: payload.isPollingForCheckResponse,
      };
    case IS_CHECK_PROCESSING:
      return {
        ...state,
        isCheckProcessing: payload,
      };
    case IS_POLLING_TIME_EXTENDED:
      return {
        ...state,
        isPollingTimeExtended: payload,
      };
    case SET_CHECK_SCAN_FEEDBACK:
      return {
        ...state,
        checkScanFeedback: payload,
      };
    case SET_CARD_PAYMENT_TYPE:
      return {
        ...state,
        cardPaymentType: payload,
      };
    case IS_AWAITING_CREDIT_CARD:
      return {
        ...state,
        isAwaitingCreditCard: true,
        cardPaymentType: payload,
      };
    case STOP_AWAITING_CREDIT_CARD:
      return {
        ...state,
        isAwaitingCreditCard: false,
      };
    case SET_AWAIT_CREDIT_CARD_TIME_REMAINING:
      return {
        ...state,
        timeRemainingToUseCard: payload,
      };
    case MAKE_CHECK_PAYMENT_ERROR:
      return {
        ...state,
        isPaymentError: true,
        isCheckProcessing: false,
        isPollingTimeExtended: false,
      };
    case SET_INSERT_CHECK_TIME_REMAINING:
      return {
        ...state,
        timeRemainingToInsertCheck: payload,
      };
    case GET_PAYMENT_BY_ID_ERROR:
      // success is handled in checkScanPollingEpic
      return {
        ...state,
        isPaymentError: true,
        isCheckProcessing: false,
        isPollingForCheckResponse: false,
        isPollingTimeExtended: false,
      };
    case STOP_CHECK_SCAN_POLL:
      return {
        ...state,
        isPollingForCheckResponse: false,
        isCheckProcessing: false,
        isPollingTimeExtended: false,
        timeRemainingToInsertCheck: initialState.timeRemainingToInsertCheck,
      };
    case GET_SERVICE_FEE:
      return {
        ...state,
        isLoading: true,
      };
    case GET_SERVICE_FEE_SUCCESS:
      return {
        ...state,
        isLoading: false,
        serviceFeeForCurrentAmount: payload.service_fee,
      };
    case GET_SERVICE_FEE_FAILURE:
      return {
        ...state,
        isLoading: false,
      };
    case GET_CONVENIENCE_FEE:
      return {
        ...state,
        isLoading: true,
      };
    case GET_CONVENIENCE_FEE_SUCCESS:
      return {
        ...state,
        isLoading: false,
        convenienceFee: payload.convenience_fee,
        maxCardAmountAllowed: payload.max_card_amount_allowed,
      };
    case GET_CONVENIENCE_FEE_FAILURE:
      return {
        ...state,
        isLoading: false,
      };
    case SET_PAYMENT_UNIQUE_IDENTIFIER:
      return {
        ...state,
        paymentAttemptUniqueId: payload.paymentAttemptUniqueId,
      };
    case START_POLLING_FOR_CC_RESPONSE:
      return {
        ...state,
        isPollingForCCResponse: true,
      };
    case STOP_POLLING_FOR_CC_RESPONSE:
      return {
        ...state,
        isPollingForCCResponse: false,
        timeRemainingToUseCard: maxSecondsToInsertCardDps,
        paymentAttemptUniqueId: initialState.paymentAttemptUniqueId,
        isCancelingTransaction: false,
      };
    case POLLING_CC_SUCCESS:
      return {
        ...state,
        isPollingForCCResponse: false,
        isPaymentError: false,
        timeRemainingToUseCard: maxSecondsToInsertCardDps,
        paymentAttemptUniqueId: initialState.paymentAttemptUniqueId,
      };
    case POLLING_CC_ERROR:
      return {
        ...state,
        isPaymentError: true,
      };
    case POST_VISIT_ERROR:
      return {
        ...state,
        isPollingForCCResponse: false,
      };
    default:
      return state;
  }
};

const noCreditCardReaderAlertMessage = {
  heading: 'Device Error',
  text: 'No credit card reader associated with this terminal',
  variant: 'error',
};

// Raw Redux Maps
export const _mapStateToProps = (state) => {
  if (state.cart != {}) {
    return {
      ...state.cart,
      ...state.terminalLogin.terminalSession,
      remainingBalance: R.pathOr(
        DEFAULT_PAYMENT_VALUE,
        ['cart', 'order', 'remaining_balance'],
        state,
      ),
    };
  }
};
export const _mapDispatchToProps = (dispatch) => ({
  makePayment: (data) => dispatch(initiatePayment(data)),
  cancelTransaction: (data) => dispatch(cancelTransactionRequest(data)),
  displayCreditCardReaderAlert: () =>
    dispatch(
      checkoutAlertBarAction(replaceAlerts(noCreditCardReaderAlertMessage)),
    ),
});

// Nested Redux Maps
const {
  reducer: nestedReducer,
  mapStateToProps,
  mapDispatchToProps,
  actions,
} = Nested.nestStates(
  {
    reducer: _reducer,
    mapStateToProps: _mapStateToProps,
    mapDispatchToProps: _mapDispatchToProps,
  },
  {
    forms: {
      cardCheckoutForm: {
        ...CardCheckoutForm,
        actionType: CARD_CHECKOUT_FORM_ACTION,
      },
      checkCheckoutForm: {
        ...CheckCheckoutForm,
        actionType: CHECK_CHECKOUT_FORM_ACTION,
      },
      cashCheckoutForm: {
        ...CashCheckoutForm,
        actionType: CASH_CHECKOUT_FORM_ACTION,
      },
      wirePaymentCheckoutForm: {
        ...WirePaymentCheckoutForm,
        actionType: WIRE_PAYMENT_CHECKOUT_FORM_ACTION,
      },
      paylinkCheckoutForm: {
        ...PayLinkCheckoutForm,
        actionType: PAYLINK_CHECKOUT_FORM_ACTION,
      },
    },
    modals: {
      checkoutModal: {
        ...ModalState,
        actionType: MODAL_ACTION,
      },
    },
    alertBars: {
      checkoutAlertBars: {
        ...AlertBarState,
        actionType: ALERT_BAR_ACTION,
      },
    },
  },
  'checkout',
);

// Nested Reducer
const reducer = (state, action) => nestedReducer(state, action);

export { reducer, mapStateToProps, mapDispatchToProps, actions };
